import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { KeyValue, KeyValueList, PageHeader, Panel, Toolbar } from '../components/Page';
import { EmptyState, ErrorState, InlineError, Spinner, TableSkeleton } from '../components/Feedback';
import {
  ApprovalReasonChip,
  ApprovalStatusChip,
  Chip,
  ConfidenceBadge,
  OutcomeChip,
  ScenarioChip,
  StatusChip,
} from '../components/Chips';
import {
  CheckboxField,
  SelectField,
  TextAreaField,
  TextField,
  enumOptions,
  parseEnumValue,
} from '../components/Form';
import { SelectableRow, TableWrap } from '../components/Table';
import { Pagination } from '../components/Pagination';
import { Modal } from '../components/Overlay';
import { DocumentPreview } from '../components/DocumentPreview';
import { LineAccountPicker } from '../components/LineAccountPicker';
import { LineItemPicker } from '../components/LineItemPicker';
import { CreateItemModal } from '../components/CreateItemModal';
import { useAuth } from '../lib/auth';
import { documents, learningAccounts, learningItems } from '../lib/endpoints';
import { useAsync, useDebounced } from '../lib/hooks';
import { useToast } from '../lib/toast';
import { ApiError, errorMessage } from '../lib/api';
import {
  DocumentStatus,
  DocumentStatusLabels,
  STALE_APPROVAL_DETAIL,
  STALE_APPROVAL_TITLE,
} from '../lib/enums';
import {
  formatDate,
  formatDateTime,
  formatDuration,
  formatMoney,
  formatNumber,
  formatRelative,
  fromDateInputValue,
  parseDecimalInput,
  toDateInputValue,
  toPercent,
} from '../lib/format';
import type {
  AiInvoiceAmountsContract,
  AiInvoiceContract,
  DocumentDetailDto,
  DocumentLineOverride,
  DocumentOverrideRequest,
} from '../lib/types';

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

type ConfidenceBand = 'all' | 'low' | 'medium' | 'high';

const CONFIDENCE_OPTIONS = [
  { value: 'all', label: 'Any confidence' },
  { value: 'high', label: 'High (90%+)' },
  { value: 'medium', label: 'Medium (70–89%)' },
  { value: 'low', label: 'Low (<70%)' },
];

function matchesBand(confidence: number | null, band: ConfidenceBand): boolean {
  if (band === 'all') return true;
  const pct = toPercent(confidence);
  if (pct === null) return band === 'low';
  if (band === 'high') return pct >= 90;
  if (band === 'medium') return pct >= 70 && pct < 90;
  return pct < 70;
}

export function PurchaseInvoicesPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('document');

  const [statusFilter, setStatusFilter] = useState<DocumentStatus | null>(DocumentStatus.ReadyForReview);
  const [band, setBand] = useState<ConfidenceBand>('all');
  const [search, setSearch] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebounced(search, 350);

  const list = useAsync(
    (signal) =>
      documents.list(
        { status: statusFilter, scenario: null, search: debouncedSearch, page, pageSize },
        signal,
      ),
    [statusFilter, debouncedSearch, page, pageSize],
  );

  // Confidence and amount are not server-side filters, so they are applied to the loaded page.
  const rows = useMemo(() => {
    const items = list.data?.items ?? [];
    const min = parseDecimalInput(minAmount);
    const max = parseDecimalInput(maxAmount);
    return items.filter((doc) => {
      if (!matchesBand(doc.confidence, band)) return false;
      if (min !== null && (doc.total ?? 0) < min) return false;
      if (max !== null && (doc.total ?? 0) > max) return false;
      return true;
    });
  }, [list.data, band, minAmount, maxAmount]);

  const select = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('document', id);
    else next.delete('document');
    setSearchParams(next, { replace: false });
  };

  if (selectedId) {
    return (
      <PurchaseInvoiceDetail
        documentId={selectedId}
        onBack={() => select(null)}
        onChanged={list.reload}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Purchase Invoices"
        description="Review AI extractions, correct them, then post to QuickBooks Online."
        actions={
          <button type="button" className="btn btn-secondary" onClick={list.reload} disabled={list.loading}>
            {list.loading ? 'Refreshing…' : 'Refresh'}
          </button>
        }
      />

      <Panel>
        <Toolbar>
          <TextField
            label="Search"
            type="search"
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder="Vendor, invoice number or file"
            className="toolbar-grow"
          />
          <SelectField
            label="Status"
            value={statusFilter === null ? '' : String(statusFilter)}
            onChange={(value) => {
              setStatusFilter(parseEnumValue<DocumentStatus>(value));
              setPage(1);
            }}
            options={enumOptions(DocumentStatusLabels)}
            placeholder="All statuses"
          />
          <SelectField
            label="Confidence"
            value={band}
            onChange={(value) => setBand(value as ConfidenceBand)}
            options={CONFIDENCE_OPTIONS}
          />
          <TextField
            label="Min amount"
            type="number"
            inputMode="decimal"
            value={minAmount}
            onChange={setMinAmount}
            placeholder="0.00"
            className="toolbar-narrow"
          />
          <TextField
            label="Max amount"
            type="number"
            inputMode="decimal"
            value={maxAmount}
            onChange={setMaxAmount}
            placeholder="0.00"
            className="toolbar-narrow"
          />
        </Toolbar>

        {list.loading && !list.data ? (
          <TableSkeleton rows={10} columns={7} />
        ) : list.error ? (
          <ErrorState message="Invoices could not be loaded." detail={list.error} onRetry={list.reload} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Nothing to review"
            description={
              (list.data?.items.length ?? 0) > 0
                ? 'Every document on this page was filtered out. Widen the confidence or amount filters.'
                : 'No documents match these filters.'
            }
          />
        ) : (
          <>
            <TableWrap busy={list.loading}>
              <thead>
                <tr>
                  <th scope="col">Vendor</th>
                  <th scope="col">Invoice</th>
                  <th scope="col">Dates</th>
                  <th scope="col" className="num">
                    Total
                  </th>
                  <th scope="col">Confidence</th>
                  <th scope="col">Status</th>
                  <th scope="col">Scenario</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((doc) => (
                  <SelectableRow
                    key={doc.id}
                    selected={false}
                    onSelect={() => select(doc.id)}
                    label={`${doc.vendorName ?? 'Unknown vendor'} ${doc.invoiceNumber ?? doc.fileName}`}
                  >
                    <td>
                      <span className="cell-strong">{doc.vendorName ?? 'Unknown vendor'}</span>
                      <span className="cell-sub">{doc.fileName}</span>
                    </td>
                    <td>
                      {doc.invoiceNumber ?? '—'}
                      {doc.hasValidationIssues ? <span className="cell-flag warn">issues</span> : null}
                    </td>
                    <td>
                      {formatDateTime(doc.invoiceDate, '—')}
                      <span className="cell-sub">
                        {doc.dueDate ? `due ${formatDateTime(doc.dueDate)}` : 'no due date'}
                      </span>
                    </td>
                    <td className="num">{formatMoney(doc.total, doc.currencyCode)}</td>
                    <td>
                      <ConfidenceBadge value={doc.confidence} size="sm" />
                    </td>
                    <td>
                      <StatusChip status={doc.status} />
                    </td>
                    <td>
                      <ScenarioChip scenario={doc.scenario} />
                    </td>
                  </SelectableRow>
                ))}
              </tbody>
            </TableWrap>

            {list.data ? (
              <Pagination
                page={list.data.page}
                pageSize={list.data.pageSize}
                totalCount={list.data.totalCount}
                totalPages={list.data.totalPages}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
              />
            ) : null}
          </>
        )}
      </Panel>
    </>
  );
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

interface LineDraft extends DocumentLineOverride {
  /** Editable; sent back as part of the override on save (see toOverrideRequest below). */
  unitOfMeasure: string | null;
  confidence: number | null;
  /**
   * iERP AI's account suggestion, display only. It becomes `qboAccountId` - the coding that is
   * saved and posted - only through the reviewer's explicit act (the option, Use, or Use all).
   */
  suggestedAccountId: string | null;
  suggestedAccountName: string | null;
  suggestedAccountConfidence: number | null;
  suggestedAccountReason: string | null;
  /**
   * UI-only: which picker the line's posting-target cell currently shows. Not part of
   * DocumentLineOverride and never sent to the server - the server derives the same thing from
   * which of qboAccountId/qboItemId is actually set, and applies its own account-wins precedence
   * if a stale draft somehow carries both. Initialised from whichever one the line already has.
   */
  lineType: 'account' | 'item';
}

interface FormDraft {
  vendorName: string;
  qboVendorId: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  currencyCode: string;
  exchangeRate: string;
  subtotal: string;
  taxAmount: string;
  total: string;
  memo: string;
  lines: LineDraft[];
}

function buildDraft(detail: DocumentDetailDto): FormDraft {
  const { summary, contract } = detail;
  const header = contract?.header;
  const amounts = contract?.amounts;

  // Human corrections are held separately from the AI contract. The effective contract already carries
  // edited descriptions and amounts, but the per-line account and item coding lives only in the saved
  // overrides — as does the memo and any explicitly chosen vendor. Overlay them here, keyed by line, or
  // a reload would silently drop a coded account the operator had already picked.
  const lineOverrides = new Map<number, DocumentLineOverride>(
    (detail.overrides?.lines ?? []).map((line) => [line.lineNo, line]),
  );

  return {
    vendorName: summary.vendorName ?? contract?.vendor.name ?? '',
    // A human's saved choice wins outright. Otherwise prefer the vendor the document is ALREADY
    // targeting (bound at intake — a handshake connection's bound vendor, or its currency-vendor
    // link) over the name-similarity suggestion below: that suggestion is a currency-blind text
    // match, and a second vendor sharing almost the same name (created for a second currency) can
    // easily outscore the one already correctly resolved. Falling back to it only when the
    // document has no vendor at all yet is what let a GBP invoice's already-correct GBP vendor
    // get silently swapped back to the near-identical USD one just by opening the review screen.
    qboVendorId:
      detail.overrides?.qboVendorId ??
      summary.qboVendorId ??
      detail.suggestedVendorMatch?.qboVendorId ??
      contract?.vendor.businessCentralVendorNo ??
      '',
    invoiceNumber: summary.invoiceNumber ?? header?.invoiceNumber ?? '',
    invoiceDate: toDateInputValue(summary.invoiceDate ?? header?.invoiceDate ?? null),
    dueDate: toDateInputValue(summary.dueDate ?? header?.dueDate ?? null),
    currencyCode: summary.currencyCode ?? header?.currencyCode ?? '',
    exchangeRate:
      detail.overrides?.exchangeRate !== null && detail.overrides?.exchangeRate !== undefined
        ? String(detail.overrides.exchangeRate)
        : '',
    subtotal: amounts?.subtotal !== null && amounts?.subtotal !== undefined ? String(amounts.subtotal) : '',
    taxAmount: amounts?.taxAmount !== null && amounts?.taxAmount !== undefined ? String(amounts.taxAmount) : '',
    total:
      summary.total !== null
        ? String(summary.total)
        : amounts?.invoiceTotal !== null && amounts?.invoiceTotal !== undefined
          ? String(amounts.invoiceTotal)
          : '',
    memo: detail.overrides?.memo ?? '',
    lines: (contract?.lines ?? []).map((line) => {
      const override = lineOverrides.get(line.lineNo);
      const qboAccountId = override?.qboAccountId ?? null;
      const qboItemId = override?.qboItemId ?? line.itemReference;
      return {
        lineNo: line.lineNo,
        description: line.description,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        lineAmount: line.lineAmount,
        qboAccountId,
        qboItemId,
        exclude: override?.exclude ?? false,
        unitOfMeasure: line.unitOfMeasure,
        confidence: line.recommendationConfidence,
        suggestedAccountId: line.suggestedAccountId ?? null,
        suggestedAccountName: line.suggestedAccountName ?? null,
        suggestedAccountConfidence: line.suggestedAccountConfidence ?? null,
        suggestedAccountReason: line.suggestedAccountReason ?? null,
        // An item coding wins the initial toggle position only when there is no account coding to
        // show instead - mirrors the server's own account-wins precedence for a line that somehow
        // carries both.
        lineType: !qboAccountId && qboItemId ? 'item' : 'account',
      };
    }),
  };
}

function toOverrideRequest(draft: FormDraft): DocumentOverrideRequest {
  return {
    vendorName: draft.vendorName.trim() || null,
    qboVendorId: draft.qboVendorId.trim() || null,
    invoiceNumber: draft.invoiceNumber.trim() || null,
    invoiceDate: fromDateInputValue(draft.invoiceDate),
    dueDate: fromDateInputValue(draft.dueDate),
    total: parseDecimalInput(draft.total),
    currencyCode: draft.currencyCode.trim().toUpperCase() || null,
    exchangeRate: parseDecimalInput(draft.exchangeRate),
    memo: draft.memo.trim() || null,
    lines: draft.lines.map((line) => ({
      lineNo: line.lineNo,
      description: line.description,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      lineAmount: line.lineAmount,
      qboAccountId: line.qboAccountId,
      qboItemId: line.qboItemId,
      exclude: line.exclude,
      unitOfMeasure: line.unitOfMeasure,
    })),
  };
}

/** Lines carrying an iERP AI account suggestion the reviewer has not acted on. */
function pendingSuggestionCount(draft: FormDraft): number {
  return draft.lines.filter((line) => line.suggestedAccountId && !line.qboAccountId && !line.exclude).length;
}

function describeLinesPanel(draft: FormDraft): string {
  const posted = draft.lines.filter((line) => !line.exclude).length;
  const suggested = draft.lines.filter((line) => line.suggestedAccountId).length;
  const base = `${posted} of ${draft.lines.length} lines will be posted.`;
  if (suggested === 0) return base;
  const scope = suggested === draft.lines.length ? 'every line' : `${suggested} of them`;
  return `${base} iERP AI suggested an account for ${scope}; a suggestion is used only when you accept it.`;
}

function statusAllowsPosting(status: DocumentStatus): boolean {
  // Posting is deliberately absent: the gateway has already claimed the write to QuickBooks and a
  // second attempt would risk a duplicate bill.
  return (
    status === DocumentStatus.ReadyForReview ||
    status === DocumentStatus.Approved ||
    status === DocumentStatus.Received ||
    status === DocumentStatus.PendingApproval
  );
}

// ---------------------------------------------------------------------------
// Line / total reconciliation
// ---------------------------------------------------------------------------

/** The server's tolerance, from QboContractMapper.Validate: two cents or less still posts. */
const RECONCILIATION_TOLERANCE = 0.02;

interface Reconciliation {
  lineSum: number;
  taxAmount: number;
  shippingAmount: number;
  discountAmount: number;
  invoiceTotal: number;
  /** What the lines have to come to: the invoice total less tax and shipping, plus discount. */
  expectedLineSum: number;
  /** lineSum − expectedLineSum, which is also (lines + tax + shipping − discount) − invoice total. */
  variance: number;
  reconciled: boolean;
}

/** Half-away-from-zero to cents, matching decimal.Round(x, 2, MidpointRounding.AwayFromZero). */
function roundToCents(value: number): number {
  // The epsilon nudges past binary representation error (1.005 * 100 is 100.49999999999999).
  const rounded = Math.round(Math.abs(value) * 100 + 1e-8);
  return (value < 0 ? -rounded : rounded) / 100;
}

/**
 * Amount the gateway will post for a line: the stated amount, else quantity × unit price, which is
 * the same fallback BuildExpenseLines and RefreshSummaryFields apply. Loose null checks because the
 * wire can omit a field entirely.
 */
function lineAmountOf(line: LineDraft): number {
  if (line.lineAmount != null) return line.lineAmount;
  if (line.quantity != null && line.unitPrice != null) {
    return roundToCents(line.quantity * line.unitPrice);
  }
  return 0;
}

type LineSortKey =
  | 'type'
  | 'no'
  | 'lineNo'
  | 'description'
  | 'quantity'
  | 'unitOfMeasure'
  | 'unitPrice'
  | 'amount'
  | 'confidence';

function lineSortValue(line: LineDraft, key: LineSortKey): string | number {
  switch (key) {
    case 'type':
      return line.lineType;
    case 'no':
      return (line.lineType === 'item' ? line.qboItemId : line.qboAccountId) ?? '';
    case 'lineNo':
      return line.lineNo;
    case 'description':
      return line.description ?? '';
    case 'quantity':
      return line.quantity ?? 0;
    case 'unitOfMeasure':
      return line.unitOfMeasure ?? '';
    case 'unitPrice':
      return line.unitPrice ?? 0;
    case 'amount':
      return lineAmountOf(line);
    case 'confidence':
      return line.confidence ?? 0;
    default:
      return '';
  }
}

function LineSortHeader({
  label,
  sortKey,
  current,
  dir,
  onSort,
  className,
  style,
  onResizeStart,
}: {
  label: string;
  sortKey: LineSortKey;
  current: LineSortKey | null;
  dir: 'asc' | 'desc';
  onSort: (key: LineSortKey) => void;
  className?: string;
  style?: CSSProperties;
  /** Present only for columns that are also drag-resizable (matches the <col> width they share). */
  onResizeStart?: (event: ReactMouseEvent<HTMLTableCellElement>) => void;
}): JSX.Element {
  const active = current === sortKey;
  return (
    <th
      scope="col"
      className={onResizeStart ? `${className ?? ''} col-resizable`.trim() : className}
      style={style}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className={active ? 'line-sort-btn active' : 'line-sort-btn'} onClick={() => onSort(sortKey)}>
        {label}
        <span className="line-sort-arrow">{active ? (dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
      {onResizeStart ? <span className="col-resize-handle" onMouseDown={onResizeStart} aria-hidden="true" /> : null}
    </th>
  );
}

/**
 * The line/total reconciliation exactly as the gateway computes it — see
 * Services/Ai/QboContractMapper.Validate and DocumentsController.RefreshSummaryFields:
 *
 *   expectedLineSum = invoiceTotal − tax − shipping + discount
 *   variance        = lineSum − expectedLineSum
 *   reconciled      = |variance| <= 0.02
 *
 * Comparing the line sum against the invoice total directly, as this screen used to, calls every
 * tax-bearing invoice broken: $100 of expense lines plus $7 of tax on a $107 invoice is correct and
 * the backend posts it.
 *
 * Tax, shipping and discount are read from the contract rather than the draft because the override
 * contract carries only the total and the lines, so these are the figures the server will use.
 * Returns null when there is nothing to judge — the same condition the server skips the check on.
 */
function reconcile(
  draft: FormDraft,
  amounts: AiInvoiceAmountsContract | undefined,
): Reconciliation | null {
  // An empty Total box does not mean "no total": the server falls back to the extracted total
  // (overrides.Total ?? contract.Amounts.InvoiceTotal), so this does too.
  const invoiceTotal = parseDecimalInput(draft.total) ?? amounts?.invoiceTotal ?? null;
  const postedLines = draft.lines.filter((line) => !line.exclude);
  if (invoiceTotal === null || postedLines.length === 0) return null;

  const lineSum = roundToCents(postedLines.reduce((sum, line) => sum + lineAmountOf(line), 0));
  const taxAmount = amounts?.taxAmount ?? 0;
  const shippingAmount = amounts?.shippingAmount ?? 0;
  const discountAmount = amounts?.discountAmount ?? 0;

  const expectedLineSum = roundToCents(invoiceTotal - taxAmount - shippingAmount + discountAmount);
  const variance = roundToCents(lineSum - expectedLineSum);

  return {
    lineSum,
    taxAmount,
    shippingAmount,
    discountAmount,
    invoiceTotal,
    expectedLineSum,
    variance,
    reconciled: Math.abs(variance) <= RECONCILIATION_TOLERANCE,
  };
}

/** "Lines 100.00 + tax 7.00 = 107.00, invoice total 107.00", carrying only the parts that apply. */
function describeReconciliation(state: Reconciliation, currencyCode: string): string {
  const money = (value: number) => formatMoney(value, currencyCode);
  const adjustments: string[] = [];
  if (state.taxAmount !== 0) adjustments.push(`+ tax ${money(state.taxAmount)}`);
  if (state.shippingAmount !== 0) adjustments.push(`+ shipping ${money(state.shippingAmount)}`);
  if (state.discountAmount !== 0) adjustments.push(`− discount ${money(state.discountAmount)}`);

  const gross = roundToCents(
    state.lineSum + state.taxAmount + state.shippingAmount - state.discountAmount,
  );

  // With nothing to add or take off, "Lines 100.00 = 100.00" would just repeat itself.
  const left =
    adjustments.length > 0
      ? `Lines ${money(state.lineSum)} ${adjustments.join(' ')} = ${money(gross)}`
      : `Lines ${money(state.lineSum)}`;

  return `${left}, invoice total ${money(state.invoiceTotal)}`;
}

function ReconciliationNote({
  state,
  currencyCode,
}: {
  state: Reconciliation;
  currencyCode: string;
}): JSX.Element {
  const comparison = describeReconciliation(state, currencyCode);

  if (state.reconciled) {
    return <p className="panel-footnote">Totals agree. {comparison}.</p>;
  }

  return (
    // No live region here: the Total field's own error message is the announced one.
    <div className="banner banner-warn banner-inline">
      <div className="banner-body">
        <p className="banner-title">
          Lines do not add up to the invoice total — out by{' '}
          {formatMoney(state.variance, currencyCode)}
        </p>
        <p className="banner-detail">{comparison}.</p>
        <p className="banner-detail">
          The lines have to come to {formatMoney(state.expectedLineSum, currencyCode)} (the invoice
          total less tax and shipping, plus discount). Correct the lines or the total before posting.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// What the AI read, before human edits
// ---------------------------------------------------------------------------

interface FieldChange {
  label: string;
  was: string;
  now: string;
}

/**
 * Every field where the effective contract now says something different from the AI's original
 * extraction. Compared on formatted values, because that is what the reviewer is being shown.
 */
function collectEdits(
  original: AiInvoiceContract | null,
  effective: AiInvoiceContract | null,
): FieldChange[] {
  if (!original || !effective) return [];

  const currency = effective.header.currencyCode ?? original.header.currencyCode;
  const text = (value: string | null) => (value && value.trim() !== '' ? value : '—');
  const money = (value: number | null) => formatMoney(value, currency);
  const changes: FieldChange[] = [];

  const add = (label: string, was: string, now: string) => {
    if (was !== now) changes.push({ label, was, now });
  };

  add('Vendor name', text(original.vendor.name), text(effective.vendor.name));
  add('Invoice number', text(original.header.invoiceNumber), text(effective.header.invoiceNumber));
  add('Invoice date', formatDate(original.header.invoiceDate), formatDate(effective.header.invoiceDate));
  add('Due date', formatDate(original.header.dueDate), formatDate(effective.header.dueDate));
  add('Currency', text(original.header.currencyCode), text(effective.header.currencyCode));
  add('Invoice total', money(original.amounts.invoiceTotal), money(effective.amounts.invoiceTotal));

  const effectiveLines = new Map(effective.lines.map((line) => [line.lineNo, line]));

  original.lines.forEach((was) => {
    const now = effectiveLines.get(was.lineNo);
    if (!now) {
      changes.push({
        label: `Line ${was.lineNo}`,
        was: `${text(was.description)} · ${money(was.lineAmount)}`,
        now: 'removed before posting',
      });
      return;
    }

    add(`Line ${was.lineNo} description`, text(was.description), text(now.description));
    add(`Line ${was.lineNo} quantity`, formatNumber(was.quantity, 2), formatNumber(now.quantity, 2));
    add(`Line ${was.lineNo} unit price`, money(was.unitPrice), money(now.unitPrice));
    add(`Line ${was.lineNo} amount`, money(was.lineAmount), money(now.lineAmount));
  });

  return changes;
}

/**
 * The original extraction alongside the current values, so a reviewer can always see what the AI
 * read and what a person changed. Deliberately a flat list rather than a diff viewer.
 */
function OriginalValuesPanel({ detail }: { detail: DocumentDetailDto }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const changes = collectEdits(detail.originalContract, detail.contract);

  if (!detail.hasHumanEdits) return null;

  return (
    <Panel
      title="Human edits"
      description={`Revision ${detail.revision}. The original extraction is kept untouched.`}
    >
      {changes.length === 0 ? (
        <p className="panel-footnote">
          Nothing that reaches QuickBooks differs from the original extraction.
        </p>
      ) : (
        <>
          <button
            type="button"
            className="btn btn-ghost btn-sm disclosure"
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
          >
            {open
              ? 'Hide what the AI originally read'
              : `Show what the AI originally read (${formatNumber(changes.length)} ${changes.length === 1 ? 'field' : 'fields'})`}
          </button>

          {open ? (
            <ul className="plain-list">
              {changes.map((change) => (
                <li key={change.label} className="plain-item">
                  <span className="cell-strong">{change.label}</span>
                  <span className="plain-meta">
                    AI read {change.was}, now {change.now}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </Panel>
  );
}

/* Line-items column resize -------------------------------------------------
   No data-grid library exists in this frontend, so the drag-to-widen affordance on the
   Description/Qty/Unit price/Amount/Account columns is hand-rolled: a per-column pixel width
   held in state, applied via <col> in a <colgroup> (the standard way to size a table's columns
   without fighting cell content), and a thin handle on each <th>'s right edge that tracks the
   drag with plain mousedown/mousemove/mouseup. Nothing is persisted except a best-effort
   localStorage mirror, which is convenience only - a missing or corrupt value just falls back
   to the CSS default, so every access is try/catch-guarded. */
// #, then Type/No., matching the standard purchase-line order (#, Type, No., Description,
// Quantity, ...) - the posting target is what a reviewer decides right after which line they're on,
// same as any accounting system's purchase line grid.
type LineColumn =
  | 'lineNo'
  | 'type'
  | 'no'
  | 'description'
  | 'quantity'
  | 'unitOfMeasure'
  | 'unitPrice'
  | 'amount';

const LINE_COLUMN_MIN_WIDTH: Record<LineColumn, number> = {
  lineNo: 50,
  type: 90,
  no: 130,
  description: 160,
  quantity: 70,
  unitOfMeasure: 70,
  unitPrice: 90,
  amount: 90,
};

// Applied only until a reviewer drags a column, at which point their width takes over (see
// lineColumnStyle below). No. defaults narrow (an account/item number is short); Description
// defaults wide, since it is normally the longest, most-read value on the line.
const LINE_COLUMN_DEFAULT_WIDTH: Partial<Record<LineColumn, number>> = {
  lineNo: 50,
  type: 100,
  no: 160,
  description: 320,
  unitOfMeasure: 80,
};

const LINE_COLUMN_WIDTHS_STORAGE_KEY = 'purchaseInvoiceLineColumnWidths';

function loadStoredLineColumnWidths(): Partial<Record<LineColumn, number>> {
  try {
    const raw = window.localStorage.getItem(LINE_COLUMN_WIDTHS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const widths: Partial<Record<LineColumn, number>> = {};
    for (const column of Object.keys(LINE_COLUMN_MIN_WIDTH) as LineColumn[]) {
      const value = (parsed as Record<string, unknown>)[column];
      if (typeof value === 'number' && Number.isFinite(value)) widths[column] = value;
    }
    return widths;
  } catch {
    return {};
  }
}

function saveStoredLineColumnWidths(widths: Partial<Record<LineColumn, number>>): void {
  try {
    window.localStorage.setItem(LINE_COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(widths));
  } catch {
    // Best-effort only - a private window or a full storage quota just means widths reset next visit.
  }
}

export function PurchaseInvoiceDetail({
  documentId,
  onBack,
  onChanged,
}: {
  documentId: string;
  onBack: () => void;
  onChanged?: () => void;
}): JSX.Element {
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const detail = useAsync((signal) => documents.get(documentId, signal), [documentId]);

  // The connected company's Chart of Accounts, read once for the whole line table: the Account
  // cell is a lookup over it, so a reviewer can verify or correct a suggestion without knowing
  // account ids by heart. One filter box narrows every line's list at the same time.
  const accounts = useAsync((signal) => learningAccounts.allEligible(signal), []);
  const items = useAsync((signal) => learningItems.allEligible(signal), []);
  const [accountFilter, setAccountFilter] = useState('');
  const [pullingItems, setPullingItems] = useState(false);

  // Which line's item cell asked to create a new item, so the "Create item" modal knows both what
  // to prefill and which line to code once creation succeeds. null means the modal is closed.
  const [creatingItemForLine, setCreatingItemForLine] = useState<number | null>(null);

  const pullItemCatalog = async () => {
    setPullingItems(true);
    try {
      const result = await learningItems.pull();
      toast.success(
        'Item catalog pulled',
        `${result.recordsPulled} read · ${result.recordsCreated} new · ${result.recordsUpdated} updated.`,
      );
      items.reload();
    } catch (error) {
      toast.error('Item catalog pull failed', errorMessage(error));
    } finally {
      setPullingItems(false);
    }
  };

  const [draft, setDraft] = useState<FormDraft | null>(null);
  const [dirty, setDirty] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [createVendorIfMissing, setCreateVendorIfMissing] = useState(false);
  const [attachSourceDocument, setAttachSourceDocument] = useState(true);
  const [referOpen, setReferOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [referEmail, setReferEmail] = useState('');
  const [referComment, setReferComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmQboId, setConfirmQboId] = useState('');
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releaseReason, setReleaseReason] = useState('');

  // The PDF preview is useful while triaging but costs the review form real width; collapsing it
  // is a pure grid-column change (see .review-layout / .review-layout-collapsed in app.css), and
  // unmounting DocumentPreview while collapsed also drops its object-URL fetch until it is shown
  // again.
  const [showPreview, setShowPreview] = useState(true);

  // Per-column widths for the line-items table, resizable by dragging each <th>'s right edge.
  // Session-only in state; the localStorage mirror is a convenience so a reviewer's preferred
  // widths survive a reload, not a requirement.
  const [lineColumnWidths, setLineColumnWidths] = useState<Partial<Record<LineColumn, number>>>(
    () => loadStoredLineColumnWidths(),
  );
  const [resizingColumn, setResizingColumn] = useState<LineColumn | null>(null);
  const columnDragOrigin = useRef<{ startX: number; startWidth: number } | null>(null);

  const beginColumnResize = (column: LineColumn) => (event: ReactMouseEvent<HTMLTableCellElement>) => {
    event.preventDefault();
    // event.currentTarget is the 8px resize-handle <span> itself, not the column - its own
    // offsetWidth is useless as a starting width. Walk up to the actual <th> for the real one.
    const headerWidth = (event.currentTarget as HTMLElement).closest('th')?.offsetWidth;
    const startWidth =
      lineColumnWidths[column] ?? headerWidth ?? LINE_COLUMN_DEFAULT_WIDTH[column] ?? LINE_COLUMN_MIN_WIDTH[column];
    columnDragOrigin.current = { startX: event.clientX, startWidth };
    setResizingColumn(column);
  };

  useEffect(() => {
    if (!resizingColumn) return undefined;

    const handleMouseMove = (event: MouseEvent) => {
      const origin = columnDragOrigin.current;
      if (!origin) return;
      const delta = event.clientX - origin.startX;
      const min = LINE_COLUMN_MIN_WIDTH[resizingColumn];
      const nextWidth = Math.max(min, Math.round(origin.startWidth + delta));
      setLineColumnWidths((current) => ({ ...current, [resizingColumn]: nextWidth }));
    };

    const handleMouseUp = () => {
      columnDragOrigin.current = null;
      setResizingColumn(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingColumn]);

  // Persist widths once a drag finishes rather than on every pixel of movement.
  useEffect(() => {
    if (resizingColumn) return;
    if (Object.keys(lineColumnWidths).length === 0) return;
    saveStoredLineColumnWidths(lineColumnWidths);
  }, [resizingColumn, lineColumnWidths]);

  const lineColumnStyle = (column: LineColumn): CSSProperties | undefined => {
    const width = lineColumnWidths[column] ?? LINE_COLUMN_DEFAULT_WIDTH[column];
    return width !== undefined ? { width } : undefined;
  };

  // Click-to-sort on the line-items table, same pattern as the Chart of Accounts screen: unsorted
  // (document order) until a header is clicked, then toggles asc/desc on repeat clicks. Sorting
  // reorders which row renders where, never the underlying draft.lines array, so updateLine(index)
  // below still always addresses the right line.
  const [lineSortKey, setLineSortKey] = useState<LineSortKey | null>(null);
  const [lineSortDir, setLineSortDir] = useState<'asc' | 'desc'>('asc');

  const toggleLineSort = (key: LineSortKey) => {
    if (key === lineSortKey) {
      setLineSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setLineSortKey(key);
      setLineSortDir('asc');
    }
  };

  const lineOrder = (lines: LineDraft[]): number[] => {
    const order = lines.map((_, i) => i);
    if (!lineSortKey) return order;
    const key = lineSortKey;
    order.sort((ia, ib) => {
      const a = lines[ia];
      const b = lines[ib];
      const av = lineSortValue(a, key);
      const bv = lineSortValue(b, key);
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return lineSortDir === 'asc' ? cmp : -cmp;
    });
    return order;
  };

  useEffect(() => {
    if (detail.data) {
      setDraft(buildDraft(detail.data));
      setDirty(false);
      setCreateVendorIfMissing(detail.data.summary.requiresVendorCreation);
    }
  }, [detail.data]);

  const readOnly = !can('documents.edit');

  const update = <K extends keyof FormDraft>(key: K, value: FormDraft[K]) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
    setDirty(true);
  };

  const updateLine = (index: number, patch: Partial<LineDraft>) => {
    setDraft((current) => {
      if (!current) return current;
      const lines = current.lines.map((line, lineIndex) =>
        lineIndex === index ? { ...line, ...patch } : line,
      );
      return { ...current, lines };
    });
    setDirty(true);
  };

  const runAction = async (name: string, action: () => Promise<void>) => {
    setPendingAction(name);
    setActionError(null);
    try {
      await action();
      onChanged?.();
    } catch (error) {
      const message = errorMessage(error);
      setActionError(message);

      // A stale save, a colleague's claim and a save already in flight each need a different
      // action from the person, so the API's own wording carries rather than a generic failure.
      const code = error instanceof ApiError ? error.code : null;
      const conflict =
        code === 'StaleRevision' || code === 'ReviewClaimed' || code === 'EditInFlight';

      toast.error(conflict ? 'Not saved' : 'Action failed', message);
    } finally {
      setPendingAction(null);
    }
  };

  if (detail.loading && !detail.data) {
    return (
      <>
        <PageHeader title="Loading document…" />
        <TableSkeleton rows={8} columns={4} />
      </>
    );
  }

  if (detail.error && !detail.data) {
    return (
      <>
        <PageHeader
          title="Purchase invoice"
          actions={
            <button type="button" className="btn btn-secondary" onClick={onBack}>
              Back to list
            </button>
          }
        />
        <ErrorState message="This document could not be loaded." detail={detail.error} onRetry={detail.reload} />
      </>
    );
  }

  if (!detail.data || !draft) {
    return <EmptyState title="Document not found" />;
  }

  const { summary, contract, history, approvals, suggestedVendorMatch } = detail.data;
  const score = contract?.readinessScore;
  const lineTotal = draft.lines
    .filter((line) => !line.exclude)
    .reduce((sum, line) => sum + lineAmountOf(line), 0);
  const reconciliation = reconcile(draft, contract?.amounts);

  const vendorOptions = buildVendorOptions(detail.data);

  // A document whose last posting outcome is unknown is an accounting quarantine: it is locked to
  // everything except viewing, refreshing reconciliation, and administrative resolution. The server
  // enforces this; the UI mirrors it so the locked actions are visibly unavailable.
  const isQuarantined = summary.status === DocumentStatus.PostingOutcomeUnknown;
  const canPost = can('documents.post') && statusAllowsPosting(summary.status) && !isQuarantined;
  const postBlockedReason =
    summary.status === DocumentStatus.Posting
      ? 'This document is being written to QuickBooks right now.'
      : isQuarantined
        ? 'This document is being reconciled with QuickBooks and is locked until that resolves.'
        : `A document in ${DocumentStatusLabels[summary.status]} cannot be posted.`;

  const refreshReconciliation = () =>
    runAction('reconcile', async () => {
      await documents.reconcile(documentId);
      toast.success('Reconciliation refreshed', 'Checked QuickBooks for this document again.');
      detail.reload();
    });

  // Both administrative resolutions are deliberately NOT one-click. Confirming requires the actual
  // QuickBooks transaction id (the server verifies it exists and its amount matches before anything is
  // marked posted); releasing requires a stated reason, because a casual release is exactly how an
  // ambiguous write becomes a duplicate. The server enforces both; these modals collect the evidence.
  const confirmPosted = () =>
    runAction('confirm', async () => {
      await documents.resolveUnknown(documentId, {
        action: 'confirm',
        qboEntityId: confirmQboId.trim() || null,
        comment: null,
      });
      setConfirmOpen(false);
      setConfirmQboId('');
      toast.success('Marked as posted', 'The document is now linked to its verified QuickBooks transaction.');
      detail.reload();
    });

  const releaseToRepost = () =>
    runAction('release', async () => {
      await documents.resolveUnknown(documentId, {
        action: 'release',
        qboEntityId: null,
        comment: releaseReason.trim(),
      });
      setReleaseOpen(false);
      setReleaseReason('');
      toast.success('Released for reposting', 'The reservation was released so the document can be posted again.');
      detail.reload();
    });

  // THE SAVE NAMES THE REVISION THIS SCREEN WAS BUILT FROM.
  //
  // Two reviewers on one invoice both load revision 3; one corrects the coding and saves revision 4;
  // the other's save - built from a screen that never saw those corrections - used to overwrite them
  // with no error anywhere. The API refuses a save whose revision has moved on, and runAction shows
  // the refusal, which names both revisions and says to reload.
  const saveDraft = () =>
    runAction('save', async () => {
      await documents.saveOverrides(
        documentId, toOverrideRequest(draft), detail.data?.revision ?? null);
      setDirty(false);
      toast.success('Draft saved', 'Your edits are stored against the document.');
      detail.reload();
    });

  // A claim is a COURTESY LOCK over editing and nothing else: it is not read by the posting gate,
  // the approval limit check or the approval-required rule, and holding one does not make anybody an
  // approver.
  const claimReview = () =>
    runAction('claim', async () => {
      const claim = await documents.claim(documentId);
      toast.success('Claimed for review', claim.message);
      detail.reload();
    });

  const releaseReview = () =>
    runAction('release-claim', async () => {
      const claim = await documents.release(documentId);
      toast.success('Released', claim.message);
      detail.reload();
    });

  const postDocument = () =>
    runAction('post', async () => {
      const response = await documents.post(documentId, {
        overrides: toOverrideRequest(draft),
        createVendorIfMissing,
        attachSourceDocument,
      });
      if (response?.success) {
        toast.success(
          'Posted to QuickBooks',
          response.qboDocNumber
            ? `Created ${response.qboDocNumber}.`
            : (response.message ?? 'The document was posted.'),
        );
        response.warnings?.forEach((warning) => toast.warn('Posting warning', warning));
        setDirty(false);
        detail.reload();
      } else {
        const message = response?.message ?? 'QuickBooks rejected the document.';
        setActionError(message);
        toast.error('Posting failed', message);
        detail.reload();
      }
    });

  const referForApproval = () =>
    runAction('refer', async () => {
      await documents.refer(documentId, {
        assignToEmail: referEmail.trim() || null,
        comment: referComment.trim() || null,
      });
      setReferOpen(false);
      setReferEmail('');
      setReferComment('');
      toast.success('Sent for approval', 'The document is in the approvals queue.');
      detail.reload();
    });

  const rejectDocument = () =>
    runAction('reject', async () => {
      await documents.reject(documentId, { reason: rejectReason.trim() });
      setRejectOpen(false);
      setRejectReason('');
      toast.success('Document rejected', 'It will not be posted to QuickBooks.');
      detail.reload();
    });

  return (
    <>
      <PageHeader
        title={summary.vendorName ?? summary.fileName}
        description={`${summary.invoiceNumber ? `Invoice ${summary.invoiceNumber} · ` : ''}${summary.fileName}`}
        actions={
          <>
            <button type="button" className="btn btn-secondary" onClick={onBack}>
              Back to list
            </button>
            <button type="button" className="btn btn-secondary" onClick={detail.reload} disabled={detail.loading}>
              {detail.loading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              type="button"
              className={showPreview ? 'btn btn-secondary' : 'btn btn-ghost'}
              onClick={() => setShowPreview((current) => !current)}
            >
              {showPreview ? 'Hide preview' : 'Show preview'}
            </button>
          </>
        }
      />

      <div className="doc-status-bar">
        <StatusChip status={summary.status} />
        {summary.status === DocumentStatus.Posting ? <Spinner label="Posting to QuickBooks" /> : null}
        <ScenarioChip scenario={summary.scenario} />
        <ConfidenceBadge value={summary.confidence ?? score?.overallScore} label="Overall confidence" />
        {detail.data.hasHumanEdits ? (
          <Chip tone="info" title="A person has changed something that affects the accounting.">
            Edited — revision {detail.data.revision}
          </Chip>
        ) : null}
        {summary.requiresVendorCreation ? <Chip tone="warn">Vendor not in QuickBooks</Chip> : null}
        {summary.hasPendingApproval ? <Chip tone="info">Approval pending</Chip> : null}
        {summary.qboDocNumber ? <Chip tone="good">QBO {summary.qboDocNumber}</Chip> : null}
        {summary.statusMessage ? <span className="doc-status-message">{summary.statusMessage}</span> : null}
      </div>

      <InlineError message={actionError} />

      <div className={showPreview ? 'review-layout' : 'review-layout review-layout-collapsed'}>
        {showPreview ? (
          <div className="review-preview">
            <DocumentPreview documentId={documentId} fileName={summary.fileName} />
          </div>
        ) : null}

        <div className="review-form">
          <Panel
            title="Extracted fields"
            description={
              readOnly ? 'Read-only: your role cannot edit extractions.' : 'Correct anything the bot got wrong before posting.'
            }
          >
            <div className="form-grid">
              <SelectField
                label="QuickBooks vendor"
                value={draft.qboVendorId}
                onChange={(value) => {
                  const option = vendorOptions.find((item) => item.value === value);
                  update('qboVendorId', value);
                  if (option && option.displayName) update('vendorName', option.displayName);
                }}
                options={vendorOptions}
                placeholder="Unmatched — create or pick a vendor"
                disabled={readOnly}
                badge={
                  suggestedVendorMatch ? (
                    <ConfidenceBadge
                      value={suggestedVendorMatch.confidence}
                      label={`Vendor match (${suggestedVendorMatch.matchMethod})`}
                      size="sm"
                    />
                  ) : undefined
                }
                hint={suggestedVendorMatch?.evidence ?? undefined}
              />

              <TextField
                label="Vendor name on document"
                value={draft.vendorName}
                onChange={(value) => update('vendorName', value)}
                readOnly={readOnly}
                badge={<ConfidenceBadge value={score?.vendorScore} label="Vendor score" size="sm" />}
              />

              <TextField
                label="Invoice number"
                value={draft.invoiceNumber}
                onChange={(value) => update('invoiceNumber', value)}
                readOnly={readOnly}
                badge={<ConfidenceBadge value={score?.headerScore} label="Header score" size="sm" />}
              />

              <TextField
                label="Currency"
                value={draft.currencyCode}
                onChange={(value) => update('currencyCode', value.toUpperCase())}
                readOnly={readOnly}
                placeholder="USD"
              />

              <TextField
                label="Exchange rate"
                type="number"
                inputMode="decimal"
                value={draft.exchangeRate}
                onChange={(value) => update('exchangeRate', value)}
                readOnly={readOnly}
                hint="Only needed when the currency above differs from your QuickBooks company's home currency. QuickBooks won't post the bill without one — the banner above will name its own published rate if a rate is required and none is set here yet."
              />

              <TextField
                label="Invoice date"
                type="date"
                value={draft.invoiceDate}
                onChange={(value) => update('invoiceDate', value)}
                readOnly={readOnly}
              />

              <TextField
                label="Due date"
                type="date"
                value={draft.dueDate}
                onChange={(value) => update('dueDate', value)}
                readOnly={readOnly}
              />

              <TextField
                label="Subtotal"
                type="number"
                inputMode="decimal"
                value={draft.subtotal}
                onChange={(value) => update('subtotal', value)}
                readOnly={readOnly}
                hint="Display only — the override contract carries the total."
              />

              <TextField
                label="Tax"
                type="number"
                inputMode="decimal"
                value={draft.taxAmount}
                onChange={(value) => update('taxAmount', value)}
                readOnly={readOnly}
                hint="Display only — reconciliation uses the extracted tax."
              />

              <TextField
                label="Total"
                type="number"
                inputMode="decimal"
                value={draft.total}
                onChange={(value) => update('total', value)}
                readOnly={readOnly}
                error={
                  reconciliation && !reconciliation.reconciled
                    ? `Out by ${formatMoney(reconciliation.variance, draft.currencyCode)} — the lines have to come to ${formatMoney(reconciliation.expectedLineSum, draft.currencyCode)}.`
                    : null
                }
              />

              <TextField
                label="Memo"
                value={draft.memo}
                onChange={(value) => update('memo', value)}
                readOnly={readOnly}
                placeholder="Optional memo written to QuickBooks"
                className="form-span-2"
              />
            </div>
          </Panel>

          <Panel
            title="Line items"
            description={describeLinesPanel(draft)}
            actions={
              <>
                <input
                  className="input input-inline"
                  value={accountFilter}
                  onChange={(event) => setAccountFilter(event.target.value)}
                  placeholder="Filter accounts or items…"
                  aria-label="Filter the account and item lists"
                  disabled={(accounts.data?.length ?? 0) === 0 && (items.data?.length ?? 0) === 0}
                />
                {!readOnly && !items.loading && (items.data?.length ?? 0) === 0 ? (
                  // The item master has never been pulled for this company - the No. cell falls back
                  // to a free-text box until it has something to look up, same as the Account cell
                  // does with an empty chart. This is the one place to fix that from.
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => void pullItemCatalog()}
                    disabled={pullingItems}
                    title="No QuickBooks items are cached yet, so the Item picker on this page falls back to free text. Pull once to enable it."
                  >
                    {pullingItems ? 'Pulling item catalog…' : 'Pull item catalog'}
                  </button>
                ) : null}
                {!readOnly && pendingSuggestionCount(draft) > 0 ? (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    title="Codes every line that has a suggestion and no account yet. Lines you already coded are left alone."
                    onClick={() => {
                      setDraft((current) =>
                        current
                          ? {
                              ...current,
                              lines: current.lines.map((line) =>
                                line.suggestedAccountId && !line.qboAccountId && !line.exclude
                                  ? {
                                      ...line,
                                      qboAccountId: line.suggestedAccountId,
                                      qboItemId: null,
                                      lineType: 'account',
                                    }
                                  : line,
                              ),
                            }
                          : current,
                      );
                      setDirty(true);
                    }}
                  >
                    Use all {pendingSuggestionCount(draft)} suggestions
                  </button>
                ) : null}
              </>
            }
          >
            {draft.lines.length === 0 ? (
              <EmptyState title="No line items" description="The extraction returned a header-only document." />
            ) : (
              <TableWrap className="lines-table">
                <colgroup>
                  {/* Fixed table layout (see .lines-table .data-table in app.css) makes .col-tiny's
                      1% collapse to nearly nothing once the other columns claim their explicit
                      pixel widths, which is what was squeezing Post's checkbox under the # header.
                      These two need a real pixel floor instead. */}
                  <col style={{ width: 44 }} />
                  <col style={lineColumnStyle('lineNo')} />
                  <col style={lineColumnStyle('type')} />
                  <col style={lineColumnStyle('no')} />
                  <col style={lineColumnStyle('description')} />
                  <col style={lineColumnStyle('quantity')} />
                  <col style={lineColumnStyle('unitOfMeasure')} />
                  <col style={lineColumnStyle('unitPrice')} />
                  <col style={lineColumnStyle('amount')} />
                  <col style={{ width: 56 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col" style={{ width: 44 }}>
                      Post
                    </th>
                    <LineSortHeader
                      label="#"
                      sortKey="lineNo"
                      current={lineSortKey}
                      dir={lineSortDir}
                      onSort={toggleLineSort}
                      style={lineColumnStyle('lineNo')}
                      onResizeStart={beginColumnResize('lineNo')}
                    />
                    <LineSortHeader
                      label="Type"
                      sortKey="type"
                      current={lineSortKey}
                      dir={lineSortDir}
                      onSort={toggleLineSort}
                      style={lineColumnStyle('type')}
                      onResizeStart={beginColumnResize('type')}
                    />
                    <LineSortHeader
                      label="No."
                      sortKey="no"
                      current={lineSortKey}
                      dir={lineSortDir}
                      onSort={toggleLineSort}
                      style={lineColumnStyle('no')}
                      onResizeStart={beginColumnResize('no')}
                    />
                    <LineSortHeader
                      label="Description"
                      sortKey="description"
                      current={lineSortKey}
                      dir={lineSortDir}
                      onSort={toggleLineSort}
                      style={lineColumnStyle('description')}
                      onResizeStart={beginColumnResize('description')}
                    />
                    <LineSortHeader
                      label="Qty"
                      sortKey="quantity"
                      current={lineSortKey}
                      dir={lineSortDir}
                      onSort={toggleLineSort}
                      className="num col-small"
                      style={lineColumnStyle('quantity')}
                      onResizeStart={beginColumnResize('quantity')}
                    />
                    <LineSortHeader
                      label="UoM"
                      sortKey="unitOfMeasure"
                      current={lineSortKey}
                      dir={lineSortDir}
                      onSort={toggleLineSort}
                      className="col-small"
                      style={lineColumnStyle('unitOfMeasure')}
                      onResizeStart={beginColumnResize('unitOfMeasure')}
                    />
                    <LineSortHeader
                      label="Unit price"
                      sortKey="unitPrice"
                      current={lineSortKey}
                      dir={lineSortDir}
                      onSort={toggleLineSort}
                      className="num col-small"
                      style={lineColumnStyle('unitPrice')}
                      onResizeStart={beginColumnResize('unitPrice')}
                    />
                    <LineSortHeader
                      label="Amount"
                      sortKey="amount"
                      current={lineSortKey}
                      dir={lineSortDir}
                      onSort={toggleLineSort}
                      className="num col-small"
                      style={lineColumnStyle('amount')}
                      onResizeStart={beginColumnResize('amount')}
                    />
                    <LineSortHeader
                      label="Conf."
                      sortKey="confidence"
                      current={lineSortKey}
                      dir={lineSortDir}
                      onSort={toggleLineSort}
                      style={{ width: 56 }}
                    />
                  </tr>
                </thead>
                <tbody>
                  {lineOrder(draft.lines).map((index) => {
                    const line = draft.lines[index];
                    return (
                    <tr key={`${line.lineNo}-${index}`} className={line.exclude ? 'line-excluded' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          className="checkbox"
                          checked={!line.exclude}
                          disabled={readOnly}
                          aria-label={`Include line ${line.lineNo} when posting`}
                          onChange={(event) => updateLine(index, { exclude: !event.target.checked })}
                        />
                      </td>
                      <td className="mono">{line.lineNo}</td>
                      <td>
                        <div className="line-account-type">
                          <select
                            className="input input-inline select input-xs"
                            value={line.lineType}
                            disabled={readOnly}
                            aria-label={`Line ${line.lineNo} posting target type`}
                            onChange={(event) => {
                              const nextType = event.target.value as 'account' | 'item';
                              // Switching the toggle clears the OTHER field immediately, so the
                              // frontend never carries both an account and an item choice for the
                              // same line - the backend enforces account-wins if it ever sees both,
                              // but the picker shown here should always match what will post.
                              updateLine(
                                index,
                                nextType === 'item'
                                  ? { lineType: 'item', qboAccountId: null }
                                  : { lineType: 'account', qboItemId: null },
                              );
                            }}
                          >
                            <option value="account">Account</option>
                            <option value="item">Item</option>
                          </select>
                        </div>
                      </td>
                      <td>
                        {line.lineType === 'item' ? (
                          <div className="line-item-cell">
                            <LineItemPicker
                              lineNo={line.lineNo}
                              value={line.qboItemId}
                              items={items.data ?? []}
                              itemsLoading={items.loading}
                              filter={accountFilter}
                              readOnly={readOnly}
                              onChange={(itemId) => updateLine(index, { qboItemId: itemId, qboAccountId: null })}
                            />
                            {!readOnly ? (
                              <button
                                type="button"
                                className="btn btn-link btn-sm"
                                onClick={() => setCreatingItemForLine(line.lineNo)}
                              >
                                + New item
                              </button>
                            ) : null}
                          </div>
                        ) : (
                          <LineAccountPicker
                            lineNo={line.lineNo}
                            value={line.qboAccountId}
                            suggestion={
                              line.suggestedAccountId
                                ? {
                                    id: line.suggestedAccountId,
                                    name: line.suggestedAccountName,
                                    confidence: line.suggestedAccountConfidence,
                                    reason: line.suggestedAccountReason,
                                  }
                                : null
                            }
                            accounts={accounts.data ?? []}
                            accountsLoading={accounts.loading}
                            filter={accountFilter}
                            readOnly={readOnly}
                            onChange={(accountId) => updateLine(index, { qboAccountId: accountId, qboItemId: null })}
                          />
                        )}
                      </td>
                      <td>
                        <input
                          className="input input-inline input-description"
                          value={line.description ?? ''}
                          readOnly={readOnly}
                          aria-label={`Line ${line.lineNo} description`}
                          title={line.description ?? ''}
                          onChange={(event) => updateLine(index, { description: event.target.value })}
                        />
                      </td>
                      <td className="num">
                        <input
                          className="input input-inline num"
                          type="number"
                          value={line.quantity ?? ''}
                          readOnly={readOnly}
                          aria-label={`Line ${line.lineNo} quantity`}
                          onChange={(event) =>
                            updateLine(index, { quantity: parseDecimalInput(event.target.value) })
                          }
                        />
                      </td>
                      <td>
                        <input
                          className="input input-inline"
                          value={line.unitOfMeasure ?? ''}
                          readOnly={readOnly}
                          aria-label={`Line ${line.lineNo} unit of measure`}
                          onChange={(event) => updateLine(index, { unitOfMeasure: event.target.value || null })}
                        />
                      </td>
                      <td className="num">
                        <input
                          className="input input-inline num"
                          type="number"
                          value={line.unitPrice ?? ''}
                          readOnly={readOnly}
                          aria-label={`Line ${line.lineNo} unit price`}
                          onChange={(event) =>
                            updateLine(index, { unitPrice: parseDecimalInput(event.target.value) })
                          }
                        />
                      </td>
                      <td className="num">
                        <input
                          className="input input-inline num"
                          type="number"
                          value={line.lineAmount ?? ''}
                          readOnly={readOnly}
                          aria-label={`Line ${line.lineNo} amount`}
                          onChange={(event) =>
                            updateLine(index, { lineAmount: parseDecimalInput(event.target.value) })
                          }
                        />
                      </td>
                      <td>
                        <ConfidenceBadge value={line.confidence} size="sm" />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={8} className="num">
                      Included lines
                    </td>
                    <td className="num strong">{formatMoney(lineTotal, draft.currencyCode)}</td>
                    <td />
                  </tr>
                  {reconciliation ? (
                    <tr>
                      <td colSpan={8} className="num">
                        Lines have to come to
                      </td>
                      <td className="num strong">
                        {formatMoney(reconciliation.expectedLineSum, draft.currencyCode)}
                      </td>
                      <td />
                    </tr>
                  ) : null}
                </tfoot>
              </TableWrap>
            )}

            {reconciliation ? (
              <ReconciliationNote state={reconciliation} currencyCode={draft.currencyCode} />
            ) : null}
          </Panel>

          <OriginalValuesPanel detail={detail.data} />

          <IssuesPanel detail={detail.data} />

          {!readOnly ? (
            <Panel title="Posting options">
              <CheckboxField
                label="Create the vendor in QuickBooks if it does not exist"
                checked={createVendorIfMissing}
                onChange={setCreateVendorIfMissing}
                hint={
                  summary.requiresVendorCreation
                    ? 'This document needs a vendor that is not in QuickBooks yet.'
                    : undefined
                }
              />
              <CheckboxField
                label="Attach the source document to the QuickBooks record"
                checked={attachSourceDocument}
                onChange={setAttachSourceDocument}
              />
            </Panel>
          ) : null}

          {summary.reviewClaimedByEmail && !summary.reviewClaimedByMe ? (
            <Panel title="Somebody else is reviewing this">
              <p className="muted" style={{ marginTop: 0 }}>
                {summary.reviewClaimedByEmail} claimed this invoice for review. Saving your changes
                now would overwrite what they are working on, so the gateway refuses the edit until
                their claim is released or lapses. An administrator can take it over.
              </p>
            </Panel>
          ) : null}

          {isQuarantined ? (
            <Panel title="Awaiting reconciliation with QuickBooks">
              <p className="muted" style={{ marginTop: 0 }}>
                The last attempt to post this document was sent to QuickBooks but no confirmation came
                back, so it is not yet known whether the transaction was created. The document is locked —
                it cannot be edited, posted, referred, rejected, retried or cancelled — while the gateway
                reconciles it against QuickBooks. This deliberately prevents a duplicate.
              </p>
              <div className="action-bar">
                {can('documents.reconcile') ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={refreshReconciliation}
                    disabled={pendingAction !== null}
                  >
                    {pendingAction === 'reconcile' ? 'Checking…' : 'Refresh reconciliation'}
                  </button>
                ) : null}

                {can('documents.resolveUnknown') ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setConfirmOpen(true)}
                      disabled={pendingAction !== null}
                      title="Administrator: confirm this write did land in QuickBooks. Requires the transaction id, which is verified."
                    >
                      Mark as posted…
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => setReleaseOpen(true)}
                      disabled={pendingAction !== null}
                      title="Administrator: confirm nothing was written, and release it to post again. Requires a stated reason."
                    >
                      Release to repost…
                    </button>
                  </>
                ) : null}
              </div>
            </Panel>
          ) : null}

          <div className="action-bar">
            {can('documents.post') ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={postDocument}
                disabled={!canPost || pendingAction !== null}
                title={canPost ? undefined : postBlockedReason}
              >
                {pendingAction === 'post' || summary.status === DocumentStatus.Posting
                  ? 'Posting…'
                  : 'Approve & Post'}
              </button>
            ) : null}

            {/*
              THE SUPPLIER DOES NOT EXIST YET, SO SET IT UP FIRST - WITHOUT UPLOADING ANYTHING AGAIN.

              This bill cannot post: there is nothing in QuickBooks to post it against. The same
              document becomes a vendor intake over the same stored file, and returns to being this
              invoice the moment the supplier is approved. Uploading the PDF a second time would
              create a second accounting artifact for one invoice, each with its own idempotency
              identity, and the two could then be posted independently.
            */}
            {can('documents.post')
            && summary.requiresVendorCreation
            && summary.vendorIntakeResumeScenario == null
            && !isQuarantined
            && summary.status !== DocumentStatus.Posted ? (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={pendingAction !== null}
                title="Set this supplier up first. The same document comes back as this invoice afterwards."
                onClick={() =>
                  void runAction('handoff', async () => {
                    const result = await documents.startVendorIntake(summary.id);
                    toast.success(result.message);

                    // TAKE THEM WHERE THEY ASKED TO GO. The button says "set up this supplier
                    // first"; leaving the operator on the invoice with a toast makes them hunt for
                    // the screen that does it. The SAME document id travels - this is the one
                    // document, now being asked a different question, not a new upload.
                    navigate(`/vendor-intake?document=${encodeURIComponent(summary.id)}`);
                  })
                }
              >
                {pendingAction === 'handoff' ? 'Setting up…' : 'Set up this supplier first'}
              </button>
            ) : null}

            {can('documents.refer') ? (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setReferOpen(true)}
                disabled={pendingAction !== null || isQuarantined}
              >
                Needs Review
              </button>
            ) : null}

            {can('documents.reject') ? (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => setRejectOpen(true)}
                disabled={pendingAction !== null || isQuarantined}
              >
                Reject
              </button>
            ) : null}

            {can('documents.edit') ? (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={saveDraft}
                // The gateway refuses edits while a write to QuickBooks is in flight or being reconciled.
                disabled={
                  pendingAction !== null ||
                  !dirty ||
                  summary.status === DocumentStatus.Posting ||
                  isQuarantined
                }
              >
                {pendingAction === 'save' ? 'Saving…' : 'Save Draft'}
              </button>
            ) : null}

            {can('documents.edit') ? (
              summary.reviewClaimedByMe ? (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={releaseReview}
                  disabled={pendingAction !== null}
                  title="Give the review up so a colleague can take it."
                >
                  {pendingAction === 'release-claim' ? 'Releasing…' : 'Release review'}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={claimReview}
                  disabled={pendingAction !== null || isQuarantined}
                  title={
                    summary.reviewClaimedByEmail
                      ? `${summary.reviewClaimedByEmail} is reviewing this. Taking it needs an administrator unless their claim has lapsed.`
                      : 'Tell colleagues you are working on this invoice.'
                  }
                >
                  {pendingAction === 'claim'
                    ? 'Claiming…'
                    : summary.reviewClaimedByEmail
                      ? 'Take the review'
                      : 'Claim for review'}
                </button>
              )
            ) : null}

            {can('documents.retry') && summary.status === DocumentStatus.Failed ? (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() =>
                  runAction('retry', async () => {
                    await documents.retry(documentId);
                    toast.success('Retry queued', 'The gateway will process the document again.');
                    detail.reload();
                  })
                }
                disabled={pendingAction !== null}
              >
                Retry
              </button>
            ) : null}

            {can('documents.edit') &&
            (summary.status === DocumentStatus.Processing ||
              summary.status === DocumentStatus.Received ||
              summary.status === DocumentStatus.NeedsMorePages) ? (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() =>
                  runAction('cancel', async () => {
                    await documents.cancel(documentId);
                    toast.success('Document cancelled');
                    detail.reload();
                  })
                }
                disabled={pendingAction !== null}
              >
                Cancel document
              </button>
            ) : null}
          </div>

          <Panel title="Document activity">
            <KeyValueList columns={2}>
              <KeyValue label="Uploaded">
                {formatDateTime(summary.createdUtc)} by {summary.uploadedByEmail ?? 'system'}
              </KeyValue>
              <KeyValue label="Posted">{summary.postedUtc ? formatDateTime(summary.postedUtc) : '—'}</KeyValue>
              <KeyValue label="External job">
                <span className="mono">{summary.externalJobId ?? '—'}</span>
              </KeyValue>
              <KeyValue label="QuickBooks entity">
                <span className="mono">{summary.qboEntityId ?? '—'}</span>
              </KeyValue>
            </KeyValueList>

            {approvals.length > 0 ? (
              <div className="sub-section">
                <h3 className="sub-title">Approvals</h3>
                <ul className="plain-list">
                  {approvals.map((approval) => (
                    <li key={approval.id} className="plain-item">
                      <ApprovalStatusChip status={approval.status} />
                      <ApprovalReasonChip reason={approval.reason} detail={approval.reasonDetail} />
                      {approval.isStale ? <Chip tone="warn">No longer valid</Chip> : null}
                      <span className="plain-meta">
                        {approval.decidedByEmail
                          ? `${approval.decidedByEmail} · ${formatRelative(approval.decidedUtc)}`
                          : `requested ${formatRelative(approval.createdUtc)}`}
                        {approval.documentRevision > 0
                          ? ` · raised against revision ${approval.documentRevision}`
                          : ''}
                      </span>
                      {approval.isStale ? (
                        <span className="plain-note">
                          {STALE_APPROVAL_TITLE}. {STALE_APPROVAL_DETAIL}
                        </span>
                      ) : null}
                      {approval.decisionComment ? (
                        <span className="plain-note">{approval.decisionComment}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="sub-section">
              <h3 className="sub-title">Sync history</h3>
              {history.length === 0 ? (
                <EmptyState title="No sync attempts yet" />
              ) : (
                <TableWrap>
                  <thead>
                    <tr>
                      <th scope="col">When</th>
                      <th scope="col">Operation</th>
                      <th scope="col">Outcome</th>
                      <th scope="col">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((entry) => (
                      <tr key={entry.id}>
                        <td>{formatDateTime(entry.createdUtc)}</td>
                        <td className="mono">{entry.operation}</td>
                        <td>
                          <OutcomeChip outcome={entry.outcome} />
                        </td>
                        <td>
                          {entry.message ?? '—'}
                          <span className="cell-sub">
                            {formatDuration(entry.durationMs)}
                            {entry.errorCode ? ` · ${entry.errorCode}` : ''}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </TableWrap>
              )}
            </div>
          </Panel>
        </div>
      </div>

      <Modal
        open={referOpen}
        onClose={() => setReferOpen(false)}
        title="Send for approval"
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setReferOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={referForApproval}
              disabled={pendingAction !== null}
            >
              {pendingAction === 'refer' ? 'Sending…' : 'Send for approval'}
            </button>
          </>
        }
      >
        <TextField
          label="Assign to (email)"
          type="email"
          value={referEmail}
          onChange={setReferEmail}
          placeholder="Leave blank to use the default approver"
        />
        <TextAreaField
          label="Comment"
          value={referComment}
          onChange={setReferComment}
          placeholder="What should the approver look at?"
        />
      </Modal>

      <Modal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title="Reject document"
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setRejectOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={rejectDocument}
              disabled={pendingAction !== null || rejectReason.trim().length === 0}
            >
              {pendingAction === 'reject' ? 'Rejecting…' : 'Reject document'}
            </button>
          </>
        }
      >
        <p className="confirm-message">
          Rejecting stops this document from ever reaching QuickBooks. The reason is recorded on the
          audit trail.
        </p>
        <TextAreaField
          label="Reason"
          value={rejectReason}
          onChange={setRejectReason}
          required
          placeholder="e.g. Duplicate of invoice 10241"
        />
      </Modal>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirm this document posted"
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={confirmPosted}
              disabled={pendingAction !== null || confirmQboId.trim().length === 0}
            >
              {pendingAction === 'confirm' ? 'Verifying…' : 'Verify and mark as posted'}
            </button>
          </>
        }
      >
        <p className="confirm-message">
          Find the transaction this document created in QuickBooks and enter its id. The gateway
          verifies the transaction exists and that its total matches what was sent before anything is
          marked posted — a document is never confirmed against a transaction that says something
          different.
        </p>
        <TextField
          label="QuickBooks transaction id"
          value={confirmQboId}
          onChange={setConfirmQboId}
          required
          placeholder="e.g. 1042"
        />
      </Modal>

      <Modal
        open={releaseOpen}
        onClose={() => setReleaseOpen(false)}
        title="Release this document to post again"
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setReleaseOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={releaseToRepost}
              disabled={pendingAction !== null || releaseReason.trim().length === 0}
            >
              {pendingAction === 'release' ? 'Releasing…' : 'Release for reposting'}
            </button>
          </>
        }
      >
        <p className="confirm-message">
          Releasing frees this invoice to be posted again. Only do this after checking QuickBooks
          directly and confirming no transaction was created — if one exists and this document is
          released, posting it again will duplicate it. Your reason is recorded on the audit trail.
        </p>
        <TextAreaField
          label="What did you verify?"
          value={releaseReason}
          onChange={setReleaseReason}
          required
          placeholder='e.g. "Searched QuickBooks for INV-1042 from Northwind Supplies; no bill exists."'
        />
      </Modal>
      <CreateItemModal
        open={creatingItemForLine !== null}
        onClose={() => setCreatingItemForLine(null)}
        defaultName={
          (draft?.lines.find((line) => line.lineNo === creatingItemForLine)?.qboItemId
            || draft?.lines.find((line) => line.lineNo === creatingItemForLine)?.description)
            ?? ''
        }
        accounts={accounts.data ?? []}
        accountsLoading={accounts.loading}
        onCreated={(created) => {
          items.reload();
          const index = draft?.lines.findIndex((line) => line.lineNo === creatingItemForLine) ?? -1;
          if (index >= 0) updateLine(index, { qboItemId: created.externalItemId, qboAccountId: null });
          toast.success('Item created', `${created.name} is now in QuickBooks and coded on this line.`);
        }}
      />
    </>
  );
}

function buildVendorOptions(detail: DocumentDetailDto): {
  value: string;
  label: string;
  displayName?: string;
}[] {
  const options = new Map<string, { value: string; label: string; displayName?: string }>();
  const match = detail.suggestedVendorMatch;

  if (match?.qboVendorId) {
    options.set(match.qboVendorId, {
      value: match.qboVendorId,
      label: `${match.displayName ?? match.qboVendorId} — best match (${Math.round(toPercent(match.confidence) ?? 0)}%)`,
      displayName: match.displayName ?? undefined,
    });
  }

  match?.alternatives.forEach((candidate) => {
    if (options.has(candidate.qboVendorId)) return;
    options.set(candidate.qboVendorId, {
      value: candidate.qboVendorId,
      label: `${candidate.displayName} — ${Math.round(toPercent(candidate.confidence) ?? 0)}% (${candidate.matchMethod})`,
      displayName: candidate.displayName,
    });
  });

  const contractVendorId = detail.contract?.vendor.businessCentralVendorNo;
  if (contractVendorId && !options.has(contractVendorId)) {
    options.set(contractVendorId, {
      value: contractVendorId,
      label: `${detail.contract?.vendor.businessCentralVendorName ?? contractVendorId} — from extraction`,
      displayName: detail.contract?.vendor.businessCentralVendorName ?? undefined,
    });
  }

  return Array.from(options.values());
}

function IssuesPanel({ detail }: { detail: DocumentDetailDto }): JSX.Element | null {
  const issues = detail.contract?.validationIssues ?? [];
  const warnings = detail.contract?.warnings ?? [];
  const companyWarning = detail.contract?.companyInformationWarning;

  if (issues.length === 0 && warnings.length === 0 && !companyWarning) return null;

  return (
    <Panel title="Validation">
      {companyWarning ? (
        <div className="banner banner-warn banner-inline">
          <div className="banner-body">
            <p className="banner-title">Company information mismatch</p>
            <p className="banner-detail">{companyWarning}</p>
          </div>
        </div>
      ) : null}

      {issues.length > 0 ? (
        <div className="sub-section">
          <h3 className="sub-title">Validation issues ({formatNumber(issues.length)})</h3>
          <ul className="issue-list">
            {issues.map((issue, index) => (
              <li key={`${issue.fieldName}-${index}`} className={`issue issue-${issue.severity.toLowerCase()}`}>
                <span className="issue-field mono">{issue.fieldName}</span>
                <span className="issue-message">{issue.message}</span>
                <Chip tone={issue.severity.toLowerCase() === 'error' ? 'bad' : 'warn'}>{issue.severity}</Chip>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {warnings.length > 0 ? (
        <div className="sub-section">
          <h3 className="sub-title">Warnings ({formatNumber(warnings.length)})</h3>
          <ul className="issue-list">
            {warnings.map((warning, index) => (
              <li key={`${warning.code}-${index}`} className="issue issue-warning">
                <span className="issue-field mono">{warning.code}</span>
                <span className="issue-message">{warning.message}</span>
                <Chip tone="warn">{warning.severity}</Chip>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Panel>
  );
}
